import _ from "lodash";
import React, { SyntheticEvent, useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { List, DropdownItemProps, Checkbox, Input, Popup, Icon, Label, Dropdown } from "semantic-ui-react";
import { OmniFilterDropdownItemProps } from "./types";

const StyledDropdown = styled(Dropdown)`
  div[aria-live="polite"] {
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    white-space: nowrap;
  }
`;

const OmniDropdownLabel = styled(Label)`
  &&&& {
    padding: 0;
    padding-right: 0.785714em;

    label {
      display: inline-block;
      padding: 0.35714286em 0.78571429em;
    }
  }
`;

const OmniDropdownInput = styled(Input)`
  &&&& {
    input {
      padding: 0;
      background-color: #e8e8e8;
      border: 0;
      border-bottom: 1px dotted #00000099;
      margin-bottom: 2px;
      width: ${(props: any) => props.value.length || 2}ch;
      max-width: 10ch;
    }
  }
`;

const OmniMulti = (props: any) => {
  const trigger = (
    <Label onClick={(e) => e.stopPropagation()}>
      {props.label}
      <Icon
        name='close'
        onClick={(e: any) => {
          e.stopPropagation();
          props.onClear();
        }}
      />
    </Label>
  );
  if (props.selectedOptions.length + props.options.length === 0) {
    return trigger;
  }
  return (
    <>
      <Popup
        style={{ maxHeight: "300px", overflowY: "auto" }}
        eventsEnabled={true}
        on='click'
        position='bottom left'
        trigger={trigger}
      >
        <h4>{props.header}</h4>
        <List>
          {props.selectedOptions
            .map((option: OmniFilterDropdownItemProps) => (
              <List.Item key={option.value as string}>
                <Checkbox
                  checked={true}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onRemove(props.facetKey, option.value as string);
                  }}
                  label={(option.text as string).replace(`${option.filter.label}:`, "")}
                />
              </List.Item>
            ))
            .concat(
              props.options.map((option: OmniFilterDropdownItemProps) => (
                <List.Item key={option.value as string}>
                  <Checkbox
                    checked={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onAdd(e, { value: [option.value] });
                    }}
                    label={(option.text as string).replace(`${option.filter.label}:`, "")}
                  />
                </List.Item>
              )),
            )}
        </List>
      </Popup>
    </>
  );
};

const OmniFreeform = (props: any) => {
  const [value, setValue] = useState(props.value || "");
  const ref = useRef<HTMLInputElement>();
  useEffect(() => {
    if (props.focus) {
      ref.current?.focus();
    }
  }, [props.focus]);
  return (
    <OmniDropdownLabel onClick={(e: MouseEvent) => e.stopPropagation()}>
      <label>{props.label}:</label>
      <OmniDropdownInput
        ref={ref}
        value={value}
        onFocus={() => props.setStealFocus(true)}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur();
          }
        }}
        onChange={(e: any, { value }: { value: string }) => {
          setValue(value);
        }}
        onBlur={(e: any) => {
          props.setStealFocus(false);
          if (!_.isEmpty(e.target.value)) {
            props.setFilterState(Object.assign({}, props.filterState, { [props.filterKey]: [e.target.value] }));
          }
        }}
      />
      <Icon name='close' onClick={() => props.onClear()} />
    </OmniDropdownLabel>
  );
};

export const OmniFilterPanel = (props: any) => {
  const dispatch = useDispatch();
  const dropdownRef = useRef();
  const { site } = useParams<{ site: string }>();
  const [stealFocus, setStealFocus] = useState(false);
  const [allowFreeformFocus, setAllowFreeformFocus] = useState(false);
  const onRemove = useCallback(
    (key: string, value: string) => {
      props.removeValue(key, value);
      props.setDirty(true);
    },
    [props.options],
  );
  const clearFacet = useCallback(
    (removeKey: string) => {
      props.setFilterState(_.pickBy(props.filterState, (values: string[], key: string) => key !== removeKey));
      props.setDirty(true);
    },
    [props.filterState, props.setFilterState],
  );
  const onChange = useCallback(
    (e: SyntheticEvent<HTMLElement, Event> | undefined, { value }: any) => {
      if ((e as any)?.key === "Backspace" && (dropdownRef?.current as any)?.searchRef?.current !== e?.target) {
        return;
      }
      if (value === undefined) {
        return;
      }
      const newState = _.pickBy(props.filterState, (values: string[], key: string) => value.includes(key));
      _.each(value, (v: string, i: number) => {
        const [filterKey, ...filterValue] = v.split(":");
        const joinedFilterValue = filterValue.join(":");
        const filter = props.filters[filterKey];
        if (props.analytics) {
          dispatch({
            type: props.analytics.changed,
            payload: { site, name: filterKey },
          });
        }
        if (filter) {
          if (filter.render === "freeform") {
            if (_.isEmpty(filterValue)) {
              return;
            }
            if (i == value.length - 1) {
              setAllowFreeformFocus(true);
            }
          }
          _.each(filter.exclusiveTo || [], (otherFilter: string) => {
            newState[otherFilter] = undefined;
          });
          newState[filterKey] = _.takeRight(
            _.compact([...(props.filterState[filterKey] || []), joinedFilterValue]),
            filter.max || Infinity,
          );
        } else {
          newState["q"] = _.takeRight((newState["q"] || []).concat(v), 1);
        }
      });
      props.setFilterState({ ...props?.filterState, ..._.pickBy(newState, _.identity) });
      props.setDirty(true);
      setAllowFreeformFocus(false);
    },
    [props.setDirty, props.setFilterState, props.filterState, dropdownRef],
  );
  const searchFn = useCallback(
    (options: DropdownItemProps[], query: string): DropdownItemProps[] => {
      return _.take(
        props.options
          .filter((option: OmniFilterDropdownItemProps) => option.text)
          .filter((option: OmniFilterDropdownItemProps) => {
            const haystack = _.compact((option.filter?.keywords || []).concat(option.text as string))
              .join("")
              .toLowerCase()
              ?.replace(/[^0-9a-zA-Z\w]/g, "");
            return _.every(query.split(" "), (needle: string) => {
              return haystack.includes(needle.toLowerCase().replace(/[^0-9a-zA-Z\w]/g, ""));
            });
          }),
        50,
      );
    },
    [props.options],
  );
  const activeOptions = useMemo(() => {
    const filterState = { ...props.filterState };
    delete filterState.types;
    delete filterState.application_id;
    delete filterState.primary_identifier;
    delete filterState.permit_number;
    delete filterState.applicant;
    delete filterState.has_balance;
    delete filterState.submitted_at;
    delete filterState.expired_at;
    delete filterState.clearfilters;
    return _.map(filterState, (values: string[], key: string) => {
      const filter = props.filters[key];
      const text =
        filter.label +
        ": " +
        _.uniq(
          _.map(values, (value: string) => {
            if (key == "q") {
              return value;
            }
            const option = props.valueToOption(`${key}:${value}`) || props.textToOption(`${filter.label}: ${value}`);
            return option?.text?.replace(`${filter.label}:`, "");
          }),
        ).join(", ");
      return {
        key,
        value: key,
        text,
        filter,
        values: _.compact(filterState[key]),
        options: _.compact(values?.map((value: string) => `${key}:${value}`).map(props.valueToOption)),
      };
    });
  }, [props.filterState, props.filters, props.textToOption, props.valueToOption]);
  const dropdownValues = useMemo(() => {
    const tempActiveOptions = activeOptions.filter((item: any) => item.key !== "types");
    return _.map(tempActiveOptions, (option: any) => option.value);
  }, [activeOptions]);
  const sortedOptions = useMemo(() => {
    const options = props.options;
    const groups = _.groupBy(options, (option: any) => option.text.split(":")[0]);
    const tempActiveOptions = activeOptions.filter((item: any) => item.key !== "types");
    return _.keys(groups)
      .map((group: string) => ({
        key: `group:${group}`,
        value: `group:${group}`,
        content: (
          <span>
            {group} -{" "}
            <em>
              Start typing {`'${group.toLowerCase()}'`} to filter on {group}
            </em>
          </span>
        ),
        disabled: true,
        icon: "lightbulb",
      }))
      .concat(_.take(options, 50))
      .concat(tempActiveOptions as []);
  }, [props.options, activeOptions]);
  return (
    <div ref={props.ref}>
      <StyledDropdown
        fluid
        selection
        multiple
        lazyLoad
        style={{ borderRadius: 0, padding: props.padding }}
        ref={dropdownRef as any}
        additionLabel='Search for '
        additionPosition='top'
        loading={props.loading}
        placeholder={
          <span className={`tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-pr-9`}>{props.header}</span>
        }
        allowAdditions={!stealFocus}
        search={!stealFocus && searchFn}
        searchInput={{ autoFocus: true }}
        value={dropdownValues}
        onChange={onChange}
        closeOnChange={true}
        openOnFocus={false}
        renderLabel={({ key, text, values, filter, options }: any) => {
          if (filter.render === "freeform") {
            return (
              <>
                <OmniFreeform
                  focus={allowFreeformFocus}
                  label={filter.label}
                  value={_.first(values)}
                  filterKey={key}
                  filterState={props.filterState}
                  onClear={() => clearFacet(key)}
                  setStealFocus={setStealFocus}
                  setFilterState={props.setFilterState}
                />
              </>
            );
          }
          if (_.isFunction(filter.render)) {
            return (
              <>
                <Label onClick={(e) => e.stopPropagation()}>
                  {filter.render({
                    values,
                    onChange: (newValue: any) => {
                      onChange(undefined, { value: [...dropdownValues, `${key}:${newValue}`] });
                    },
                  })}
                  <Icon name='close' onClick={() => clearFacet(key)} />
                </Label>
              </>
            );
          }
          return (
            <OmniMulti
              facetKey={key}
              header={filter.label}
              label={text}
              onClear={() => clearFacet(key)}
              onRemove={onRemove}
              onAdd={onChange}
              selectedOptions={options}
              options={props.options.filter((option: any) => option.key.split(":")[0] === key)}
            />
          );
        }}
        options={sortedOptions}
      />
    </div>
  );
};
